import { fetchToursPopularDestinations } from 'actions/GlobalSearch/GlobalSearchToursActions'
import clsx from 'clsx'
import HiddenInput from 'components/Common/Form/Input/HiddenInput'
import InputLabelWrap from 'components/Common/Form/Input/InputLabelWrap'
import TextInput from 'components/Common/Form/Input/TextInput'
import TextInputOnly from 'components/Common/Form/Input/TextInputOnly'
import IconButton from 'components/Luxkit/Button/IconButton'
import CheckboxButton from 'components/Luxkit/Checkbox/CheckboxButton'
import DropdownList from 'components/Luxkit/Dropdown/List/DropdownList'
import LineSearchIcon from 'components/Luxkit/Icons/line/LineSearchIcon'
import LineTimesIcon from 'components/Luxkit/Icons/line/LineTimesIcon'
import ListItem from 'components/Luxkit/List/ListItem'
import ListItemLoadingSkeleton from 'components/Luxkit/List/ListItemLoadingSkeleton'
import ListSubheader from 'components/Luxkit/List/ListSubheader'
import Modal from 'components/Luxkit/Modal/Modal'
import TextLink from 'components/Luxkit/TextLink'
import BodyText from 'components/Luxkit/Typography/BodyText'
import InputText from 'components/Luxkit/Typography/InputText'
import Group from 'components/utils/Group'
import { ANYWHERE_PLACE_ID } from 'constants/search'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import useSearchTypeahead, { SearchTypeaheadOptions } from 'hooks/Search/useSearchTypeahead'
import { useScreenSizeOnly } from 'hooks/useScreenSize'
import useToggle, { useMultiValueToggle } from 'hooks/useToggle'
import { EmptyArray, take, unique, without } from 'lib/array/arrayUtils'
import { rem } from 'polished'
import React, { ChangeEventHandler, MouseEventHandler, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { getToursPopularDestinations } from 'selectors/destinationSelectors'
import styled from 'styled-components'

const FieldContainer = styled.div`
  display: grid;
  grid-template: auto / 1fr;
  justify-content: stretch;
  align-items: start;
`

const DisplayField = styled(InputText)`
  grid-column: 1 / -1;
  grid-row: 1 / -1;
  pointer-events: none;
  /* fake input look */
  height: ${rem(44)};
  margin: 1px ${rem(24)} 1px 1px;
  padding-left: ${rem(12)};
  width: calc(100% - ${rem(48)}); /* Why not 100%? To reserve space for the action on the end. */
  background-color: ${props => props.theme.palette.neutral.default.eight};
  border: 1px solid transparent;
  z-index: 1;
  display: flex;
  align-items: center;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
  &:empty {
    display: none;
  }
`

const EditField = styled(TextInputOnly)`
  grid-column: 1 / -1;
  grid-row: 1 / -1;

  &:focus-within + ${DisplayField},
  &:active + ${DisplayField},
  &.expanded + ${DisplayField} {
    opacity: 0;
    z-index: -1;
  }
`

const regionName = new Intl.DisplayNames('en', { type: 'region', fallback: 'code' })

const MAX_VALUES_TO_DISPLAY: number = 2
const LABEL = 'Destinations you are interested in visiting'
const TYPEAHEAD_OPTIONS: SearchTypeaheadOptions = {
  vertical: 'tour',
  types: ['city', 'province_state', 'country', 'continent'],
  searchTypes: ['destination'],
  limit: 20,
}

interface TypeaheadOption {
  title: string
  subtitle?: string
  value: string
}

export function parseTailorMadeTourDestinationsValue(value: string): App.Tours.TailorMadeTourRequest['destinations'] {
  return value.split('+')
}

interface Props {
  name: string
  defaultValue?: App.Tours.TailorMadeTourRequest['destinations']
  tourOfferVariation?: App.Tours.TourV2OfferVariation
}

function TailorMadeTourDestinationsInput({ name, defaultValue, tourOfferVariation }: Props) {
  const isMobile = useScreenSizeOnly('mobile')
  const labelRef = useRef<HTMLLabelElement>(null)
  const editorInputRef = useRef<HTMLInputElement>(null)
  const dispatch = useAppDispatch()
  const [searchTerm, setSearchTerm] = useState<string>('')
  const { toggle: toggleValue, setCopy: selection, set: setSelection, arrayValues: selectedValues } = useMultiValueToggle<string>(defaultValue)
  const tailorMadeTourOfferDestinations = useMemo<Array<string>>(() => {
    if (!tourOfferVariation) return EmptyArray
    return unique(tourOfferVariation.itinerary.flatMap(i => {
      return i.locationsVisitedDetails.map(location => {
        if (location.countryCode) {
          try {
            return `${location.name}, ${regionName.of(location.countryCode)}`
          } catch {
            return `${location.name}, ${location.countryCode}`
          }
        }
        else {
          return location.name
        }
      })
    }))
  }, [tourOfferVariation])
  useEffect(() => {
    if (tailorMadeTourOfferDestinations.length) {
      toggleValue(...tailorMadeTourOfferDestinations)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tailorMadeTourOfferDestinations])
  const { value: expanded, on: expand, off: collapse } = useToggle(false)
  const toursPopularDestinations = useAppSelector(getToursPopularDestinations)
  const popularDestinations = useMemo<Array<string>>(() => {
    return toursPopularDestinations
      .filter(item => item.value !== ANYWHERE_PLACE_ID)
      .map(item => {
        if (item.format.secondaryText) return `${item.format.mainText}, ${item.format.secondaryText}`
        return item.format.mainText
      })
  }, [toursPopularDestinations])
  const searchTypeahead = useSearchTypeahead(searchTerm, TYPEAHEAD_OPTIONS)
  const typeaheadOptions = useMemo<Array<TypeaheadOption>>(() => {
    return searchTypeahead.data?.searchItems.map<TypeaheadOption>((searchItem) => {
      let value = searchItem.format.mainText
      if (searchItem.format.secondaryText && searchItem.format.secondaryText !== searchItem.format.mainText) {
        value += `, ${searchItem.format.secondaryText}`
      }
      return {
        value,
        title: searchItem.format.mainText,
        subtitle: searchItem.format.secondaryText,
      }
    }) ?? EmptyArray
  }, [searchTypeahead])

  const handleSearchTermChange = useCallback<ChangeEventHandler<HTMLInputElement>>((event) => {
    setSearchTerm(event.currentTarget.value)
  }, [])

  const handleItemClick = useCallback<MouseEventHandler<HTMLButtonElement>>((event) => {
    toggleValue(event.currentTarget.dataset.value!)
  }, [toggleValue])

  const resetSelection = useCallback(() => {
    setSelection([])
  }, [setSelection])

  const clearSearchTerm = useCallback<MouseEventHandler<HTMLButtonElement>>((event) => {
    event.preventDefault()
    expand()
    setSearchTerm('')
  }, [expand])

  const [displayValue, serialisedValue] = useMemo<[displayValue: string, serialisedValue: string]>(() => {
    let displayValue = take(selectedValues, MAX_VALUES_TO_DISPLAY).join(', ')
    if (selectedValues.length > MAX_VALUES_TO_DISPLAY) {
      displayValue += ` +${selectedValues.length - MAX_VALUES_TO_DISPLAY} more`
    }
    return [displayValue, selectedValues.join('+')] as const
  }, [selectedValues])
  const idleOptions = useMemo(() => {
    const possibleOptions = unique([...tailorMadeTourOfferDestinations, ...popularDestinations])
    const displayableSelectedValues = without(selectedValues, ...possibleOptions)

    return [...displayableSelectedValues, ...possibleOptions]
  }, [popularDestinations, selectedValues, tailorMadeTourOfferDestinations])

  useEffect(() => {
    dispatch(fetchToursPopularDestinations())
  }, [dispatch])

  const handleErrorUpdate = useCallback((message: string | undefined) => {
    editorInputRef.current?.setCustomValidity(message ?? '')
  }, [])

  const body = <>
    {!searchTerm && idleOptions.map((value) => <ListItem
      key={value}
      size="large"
      title={value}
      endIcon={<CheckboxButton checked={selection.has(value)} />}
      data-value={value}
      onClick={handleItemClick}
    />)}
    {!!searchTerm && <>
      {searchTypeahead.fetching && <>
        <ListItemLoadingSkeleton size="large" />
        <ListItemLoadingSkeleton size="large" />
        <ListItemLoadingSkeleton size="large" />
        <ListItemLoadingSkeleton size="large" />
        <ListItemLoadingSkeleton size="large" />
      </>}
      {(!!searchTypeahead.data?.searchTerm && !typeaheadOptions.length) && <ListSubheader>
        Could not find any results for "{searchTypeahead.data.searchTerm}"
      </ListSubheader>}
      {typeaheadOptions.map((searchItem) => <ListItem
        key={searchItem.value}
        size="large"
        title={searchItem.title}
        subtitle={searchItem.subtitle}
        endIcon={<CheckboxButton checked={selection.has(searchItem.value)} />}
        data-value={searchItem.value}
        onClick={handleItemClick}
      />)}
    </>}
  </>

  return <>
    <InputLabelWrap label={LABEL} optionalIndicatorEnabled={false} ref={labelRef}>
      <FieldContainer>
        <EditField
          ref={editorInputRef}
          value={searchTerm}
          placeholder="Search destinations"
          className={clsx({ expanded })}
          endIcon={<>
            {!searchTerm && <LineSearchIcon />}
            {!!searchTerm && <IconButton
              type="button"
              kind="tertiary"
              size="small"
              horizontalOutdent="end"
              onClick={clearSearchTerm}
            >
              <LineTimesIcon />
            </IconButton>}
          </>}
          onChange={handleSearchTermChange}
          onFocus={expand}
          onClick={expand}
        />
        <DisplayField variant="text-regular">{displayValue}</DisplayField>
      </FieldContainer>
    </InputLabelWrap>
    <HiddenInput required name={name} value={serialisedValue} onErrorUpdate={handleErrorUpdate} />
    <DropdownList
      size="fill-anchor"
      headerExtension={<Group direction="horizontal" horizontalAlign="space-between" verticalAlign="center" gap={16}>
        <BodyText variant="medium" colour="neutral-two" wrap="truncate">
          {!!displayValue && `${displayValue} selected`}
          {!displayValue && 'No selection'}
        </BodyText>
        {!!selection.size && <TextLink size="medium" onClick={resetSelection}>Reset</TextLink>}
      </Group>}
      anchorRef={labelRef}
      triggerRef={editorInputRef}
      open={expanded && !isMobile}
      onClose={collapse}
    >
      {body}
    </DropdownList>
    <Modal
      title={LABEL}
      subtitle={<>
        {!!displayValue && `${displayValue} selected`}
        {!displayValue && 'No selection'}
      </>}
      height="max"
      isOpen={expanded && isMobile}
      onClose={collapse}
      headerExtension={<TextInput
        value={searchTerm}
        placeholder="Search destinations"
        endIcon={<>
          {!searchTerm && <LineSearchIcon />}
          {!!searchTerm && <IconButton
            kind="tertiary"
            size="small"
            horizontalOutdent="end"
            onClick={clearSearchTerm}
          >
            <LineTimesIcon />
          </IconButton>}
        </>}
        optionalIndicatorEnabled={false}
        noValidationSpacing
        noValidationMessage
        onChange={handleSearchTermChange}
      />}
      primaryActionText="Select"
      onPrimaryActionClick={collapse}
      secondaryActionText="Reset"
      isSecondaryActionHidden={!selection.size}
      onSecondaryActionClick={resetSelection}
    >
      {body}
    </Modal>
  </>
}

export default TailorMadeTourDestinationsInput
